import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import {
  Phone, PhoneOff, Mic, MicOff, Video, VideoOff,
  Monitor, Grid3X3, ChevronDown, ChevronUp,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useUIStore } from "../../stores/uiStore";
import { DtmfPad } from "./DtmfPad";
import { playRingtone, playRingback, stopCallAudio } from "../../lib/callAudio";

const STATUS_LABEL: Record<number, string> = {
  0: "No call",
  1: "Incoming call",
  2: "Calling…",
  3: "Connecting…",
  4: "Connected",
  5: "Call failed",
};

export const CallWindow = ({ accounts }: { accounts: Record<string, any> }) => {
  const { activeCall, setActiveCall } = useUIStore();
  const [status, setStatus] = useState<number>(0);
  const [muted, setMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [showDtmf, setShowDtmf] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeCall) return;
    const { session, accountId, isIncoming } = activeCall;
    const client = accounts[accountId]?.client;
    if (!client) return;

    const initialStatus = session.callStatus?.() ?? (isIncoming ? 1 : 2);
    setStatus(initialStatus);

    if (initialStatus === 1) playRingtone();
    else if (initialStatus === 2) playRingback();

    const tokens = [
      client.addCallUpdateStatusListener?.((s: any) => {
        if (s.get_sid?.() === session.get_sid?.()) {
          const st = s.callStatus?.() ?? 0;
          setStatus(st);
          if (st === 4 || st === 5 || st === 0) stopCallAudio();
        }
      }),
      client.addCallTrackListener?.((s: any, track: MediaStreamTrack) => {
        if (s.get_sid?.() !== session.get_sid?.()) return;
        const stream = new MediaStream([track]);
        if (track.kind === "audio" && audioRef.current) {
          audioRef.current.srcObject = stream;
          audioRef.current.play().catch(() => {});
        } else if (track.kind === "video" && videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }),
      client.addCallRetractListener?.((callId: string) => {
        if (callId === session.get_sid?.()) {
          stopCallAudio();
          setActiveCall(null);
        }
      }),
    ].filter(Boolean);

    return () => {
      stopCallAudio();
      tokens.forEach((t: any) => client.removeEventListener(t));
    };
  }, [activeCall, accounts]);

  if (!activeCall) return null;

  const { session, isIncoming, chatId } = activeCall;
  const isConnected = status === 4;
  const isFailed = status === 5;

  const hangup = () => {
    stopCallAudio();
    session.hangup?.();
    setActiveCall(null);
  };

  const accept = () => {
    stopCallAudio();
    session.accept?.();
    setStatus(3);
  };

  const toggleMute = () => {
    session.audioTracks?.().forEach((t: MediaStreamTrack) => { t.enabled = muted; });
    setMuted(!muted);
  };

  const toggleVideo = async () => {
    if (videoEnabled) {
      session.videoTracks?.().forEach((t: MediaStreamTrack) => { t.stop(); });
      setVideoEnabled(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        session.addMedia?.([stream]);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.play().catch(() => {});
        }
        setVideoEnabled(true);
      } catch {}
    }
  };

  const toggleScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia();
      session.addMedia?.([stream]);
      stream.getVideoTracks()[0].onended = () => {
        setActiveCall({ ...activeCall, screenSharing: false });
      };
      setActiveCall({ ...activeCall, screenSharing: true });
    } catch {}
  };

  const initials = chatId.split("@")[0].slice(0, 2).toUpperCase();

  return (
    <Draggable nodeRef={nodeRef} handle=".drag-handle" bounds="parent">
      <div ref={nodeRef} className="fixed bottom-6 right-6 z-50" style={{ width: 320 }}>
        <audio ref={audioRef} autoPlay />
        <div className="glass-panel rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
          <div className="drag-handle flex items-center justify-between px-4 py-2 cursor-move bg-white/5">
            <span className="text-xs text-white/40 select-none">Call</span>
            <button onClick={() => setExpanded(!expanded)} className="text-white/40 hover:text-white/70">
              {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
          </div>

          {expanded && (
            <div className="p-4 flex flex-col items-center gap-4">
              <video ref={videoRef} autoPlay playsInline className={cn("w-full rounded-lg bg-black", videoRef.current?.srcObject ? "block" : "hidden")} />
              {!videoRef.current?.srcObject && (
                <div className="w-16 h-16 rounded-full bg-primary/50 flex items-center justify-center text-white text-xl font-medium">
                  {initials}
                </div>
              )}

              {videoEnabled && (
                <video ref={localVideoRef} autoPlay playsInline muted className="w-24 h-18 rounded-lg absolute top-12 right-4 border border-white/20" />
              )}

              <div className="text-center">
                <p className="text-white font-medium text-sm truncate max-w-[240px]">{chatId}</p>
                <p className={cn("text-xs mt-1", isFailed ? "text-red-400" : "text-white/60")}>
                  {STATUS_LABEL[status] ?? "Calling…"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {isIncoming && status === 1 && (
                  <button onClick={accept} className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center transition-colors" title="Accept">
                    <Phone size={20} className="text-white" />
                  </button>
                )}
                {isConnected && (
                  <>
                    <button onClick={toggleMute} className={cn("w-10 h-10 rounded-full flex items-center justify-center transition-colors", muted ? "bg-white/20" : "bg-white/10 hover:bg-white/20")} title={muted ? "Unmute" : "Mute"}>
                      {muted ? <MicOff size={16} className="text-white" /> : <Mic size={16} className="text-white/60" />}
                    </button>
                    <button onClick={toggleVideo} className={cn("w-10 h-10 rounded-full flex items-center justify-center transition-colors", videoEnabled ? "bg-white/20" : "bg-white/10 hover:bg-white/20")} title={videoEnabled ? "Stop video" : "Start video"}>
                      {videoEnabled ? <VideoOff size={16} className="text-white" /> : <Video size={16} className="text-white/60" />}
                    </button>
                    <button onClick={toggleScreenShare} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" title="Share screen">
                      <Monitor size={16} className="text-white/60" />
                    </button>
                    <button onClick={() => setShowDtmf(!showDtmf)} className={cn("w-10 h-10 rounded-full flex items-center justify-center transition-colors", showDtmf ? "bg-white/20" : "bg-white/10 hover:bg-white/20")} title="Keypad">
                      <Grid3X3 size={16} className={showDtmf ? "text-white" : "text-white/60"} />
                    </button>
                  </>
                )}
                <button onClick={hangup} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center transition-colors" title="Hang up">
                  <PhoneOff size={20} className="text-white" />
                </button>
              </div>

              {showDtmf && isConnected && <DtmfPad session={session} />}
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};
